import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Les néons sont leur propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'doorways',
  family: 'geometric',
  danceability: 0.82,
  requiresTier: 0,
  energyLevel: 0.78,
  bpmMin: 110,
  bpmMax: 160,
  movement: 0.80,
  tonality: 0.50,
  enabled: true
};

// DOORWAYS : un zoom infini à travers des salles emboîtées. Chaque salle est
// une pièce en néon avec une porte au fond ; par la porte, la salle suivante,
// plus petite, et ainsi de suite. On avance de porte en porte, une par
// mesure. L'effet Droste, en architecture. Techno, progressive, trance,
// électro. LOOK VDJ party.
//
// L'EMBOÎTEMENT (critère 1) : la salle n est la même boîte (demi-largeur W,
// demi-hauteur H, profondeur D) à l'échelle k^n, son ouverture posée
// EXACTEMENT dans le mur du fond de la salle n-1 : le décalage en z est
// -D (1 - k^n) / (1 - k). La porte de la salle n est la section de la salle
// n+1 (k W x k H) ; ses formes (rectangle, arche, cercle, losange) y sont
// inscrites - on ne voit jamais à côté de la salle suivante.
//
// LE ZOOM INFINI (critère 2) : la caméra va de l'ouverture (z = 0) à la porte
// (z = -D) sur une loi EXPONENTIELLE vers le point de fuite Z∞ = -D/(1-k) :
// z(t) = Z∞ (1 - k^t). Arrivée à la porte, tout est divisé par k et décalé
// d'une salle : la scène est exactement semblable à elle-même, l'image
// est identique de part et d'autre - pas de saut, pas de couture, pas de
// perte de précision. Les styles et couleurs sont indexés sur la profondeur
// ABSOLUE (salles franchies + n) : ils suivent la renormalisation. On rend
// les salles n = -3..5 : les trois d'avant servent au demi-tour du drop.
//
// LES LIGNES (R1, critère 5) : tout est dans le fragment shader, distance au
// trait en pixels par fwidth des coordonnées de la face : nettes à toutes
// les profondeurs ; un motif fin (grille, marches) s'éteint quand son pas
// passe sous 8 px - jamais de moiré au loin.

const TAU = Math.PI * 2;
const W = 1.6, H = 1.0, D = 3.2, K = 0.28;
const ZINF = -D / (1 - K);
const STYLES = ['grid', 'stripes', 'arch', 'stairs', 'mirror', 'cover'];
const SHAPES = ['rect', 'arch', 'circle', 'diamond'];
const N_BACK = 3;
const TIER = {
  0: { ahead: 4, mirror: false },
  1: { ahead: 6, mirror: true },
  2: { ahead: 6, mirror: true }
};
const FOV_DEG = 64;
const NO_BPM = 128, SNAP_BEATS = 0.35, BUILD_BEATS = 32;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const DROP_LIT_BEATS = 8, TURN_BEATS = 1.5, TURN_HOLD = 1, ALIGN_BEATS = 4;

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hash(n) { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); }
// the style of the room at ABSOLUTE depth a: a cover room one in five, never
// twice the same style in a row
export function styleAt(a, mirrorOk) {
  if (((a % 5) + 5) % 5 === 4) return 5;
  const pool = mirrorOk ? [0, 1, 2, 3, 4] : [0, 1, 2, 3];
  const prev = ((a - 1) % 5 + 5) % 5 === 4 ? 5 : pool[Math.floor(hash(a - 1) * pool.length) % pool.length];
  let s = pool[Math.floor(hash(a) * pool.length) % pool.length];
  if (s === prev) s = pool[(pool.indexOf(s) + 1) % pool.length];
  return s;
}
// the camera's z for a progression t in [0, 1) through the current room
export function cameraZ(t) { return ZINF * (1 - Math.pow(K, t)); }
export function roomOffset(n) { return -D * (1 - Math.pow(K, n)) / (1 - K); }

// --- geometry: the room's faces, in room units; aFace = 0 floor, 1 ceiling,
// 2 left, 3 right, 4 back, 5 stairs ramp -------------------------------------------
function roomGeometry(rooms) {
  const quads = [
    [0, [-W, -H, 0], [W, -H, 0], [W, -H, -D], [-W, -H, -D]],
    [1, [-W, H, -D], [W, H, -D], [W, H, 0], [-W, H, 0]],
    [2, [-W, -H, -D], [-W, H, -D], [-W, H, 0], [-W, -H, 0]],
    [3, [W, -H, 0], [W, H, 0], [W, H, -D], [W, -H, -D]],
    [4, [-W, -H, -D], [W, -H, -D], [W, H, -D], [-W, H, -D]],
    [5, [-K * W, -H, -D * 0.55], [K * W, -H, -D * 0.55], [K * W, -K * H, -D], [-K * W, -K * H, -D]]
  ];
  const pos = [], face = [], idx = [];
  quads.forEach(([f, a, b, c, d], q) => {
    for (const v of [a, b, c, d]) { pos.push(...v); face.push(f); }
    const o = q * 4; idx.push(o, o + 1, o + 2, o, o + 2, o + 3);
  });
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('aFace', new THREE.Float32BufferAttribute(face, 1));
  g.setIndex(idx);
  const iN = new Float32Array(rooms.length); rooms.forEach((n, i) => { iN[i] = n; });
  g.setAttribute('iN', new THREE.InstancedBufferAttribute(iN, 1));
  g.instanceCount = rooms.length;
  return g;
}

// --- GLSL -----------------------------------------------------------------------
const roomVertexShader = `
  precision highp float;
  attribute float aFace;
  attribute float iN;
  uniform float uStyle[${N_BACK + 7}];      // per room slot (n + ${N_BACK})
  uniform vec3 uColor[${N_BACK + 7}];
  uniform float uAhead;
  uniform float uReflect;                   // 1: the mirror pass (y flipped about the floor)
  varying vec3 vLocal;
  varying float vFace;
  varying float vN;
  varying float vStyle;
  varying vec3 vColor;
  void main() {
    int slot = int(iN + ${N_BACK}.5);
    float style = uStyle[slot];
    vec3 p = position;
    vStyle = style; vColor = uColor[slot]; vFace = aFace; vN = iN;
    bool dead = iN > uAhead - 0.5;
    // the stairs' ramp only in a stairs room
    if (aFace > 4.5 && abs(style - 3.0) > 0.5) dead = true;
    if (uReflect > 0.5) {
      if (abs(style - 4.0) > 0.5 || aFace < 0.5) dead = true;   // mirror rooms only, not the floor itself
      p.y = -2.0 * ${H.toFixed(2)} - p.y;
    }
    if (dead) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vLocal = p;
    float s = pow(${K.toFixed(3)}, iN);
    vec3 w = p * s + vec3(0.0, 0.0, ${(-D).toFixed(3)} * (1.0 - s) / ${(1 - K).toFixed(3)});
    gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
  }
`;
const roomFragmentShader = `
  precision highp float;
  uniform float uShape;          // the doors' shape (0 rect, 1 arch, 2 circle, 3 diamond)
  uniform float uT;              // progression through the current room
  uniform float uPulse;          // the beat (the current room's edges)
  uniform float uLit;            // the drop: every room lit
  uniform float uTime;
  uniform float uReflect;
  uniform sampler2D uCover;
  uniform float uHasCover;
  varying vec3 vLocal;
  varying float vFace;
  varying float vN;
  varying float vStyle;
  varying vec3 vColor;

  // the door: inside < 0 (room units on the back wall), inscribed in k W x k H
  float doorSdf(vec2 p, float shape) {
    vec2 h = vec2(${(K * W).toFixed(4)}, ${(K * H).toFixed(4)});
    if (shape < 0.5) { vec2 q = abs(p) - h; return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0); }
    if (shape < 1.5) {
      // an arch: the rectangle below the centre, a half ellipse above
      if (p.y < 0.0) { vec2 q = abs(p) - h; return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0); }
      vec2 e = p / h; return (length(e) - 1.0) * min(h.x, h.y);
    }
    if (shape < 2.5) return length(p) - h.y;
    return (abs(p.x) / h.x + abs(p.y) / h.y - 1.0) * min(h.x, h.y) * 0.85;
  }
  // a neon line at distance d px, half width h: core, sheath, halo (R1)
  vec3 neon(float d, float h, vec3 col, float gain) {
    float cov = clamp(h + 0.5 - d, 0.0, 1.0);
    float core = 1.0 - smoothstep(max(h * 0.4, 0.5) - 0.5, max(h * 0.4, 0.5) + 0.5, d);
    float halo = 0.30 * exp(-max(d - h, 0.0) / 3.0) * (1.0 - smoothstep(12.0, 18.0, max(d - h, 0.0)));
    return (mix(col, vec3(0.92, 0.90, 0.86), core * step(1.2, h)) * cov + col * halo) * gain;
  }
  float px(float c, float c0, float fw) { return abs(c - c0) / max(fw, 1e-7); }
  // fine patterns die out when their period falls under 8 px
  float fineFade(float period, float fw) { return smoothstep(4.0, 9.0, period / max(fw, 1e-7)); }
  void main() {
    vec3 L = vLocal;
    float face = vFace;
    vec3 col = vColor;
    // the door glows as the camera reaches it (s: rooms until crossing)
    float sDoor = vN + 1.0 - uT, sFront = vN - uT;
    float gDoor = exp(-max(sDoor, 0.0) / 0.10) * step(-0.05, sDoor);
    float gFront = exp(-max(sFront, 0.0) / 0.10) * step(-0.05, sFront);
    float edgeGain = 1.0 + 0.45 * uPulse * step(abs(vN), 0.5) + 0.5 * uLit;
    float wallBase = 0.03 + 0.05 * uLit;
    vec2 a; float fwA, fwB;
    if (face < 1.5) a = vec2(L.x, L.z); else if (face < 3.5) a = vec2(L.z, L.y); else if (face < 4.5) a = vec2(L.x, L.y); else a = vec2(L.x, L.z);
    fwA = fwidth(a.x); fwB = fwidth(a.y);

    // the back wall: the door is a hole (the next room shows through)
    float shape = abs(vStyle - 2.0) < 0.5 ? 1.0 : uShape;
    float dd = 1.0;
    if (face > 3.5 && face < 4.5) {
      dd = doorSdf(L.xy, shape);
      if (dd < 0.0) discard;
    }
    vec3 c = col * wallBase;
    // the cover room: the side walls show the album art, big
    if (abs(vStyle - 5.0) < 0.5 && face > 1.5 && face < 3.5 && uHasCover > 0.5) {
      vec2 uv = vec2((face < 2.5 ? -L.z : D_PLUS_Z(L.z)) / ${(2 * H).toFixed(2)}, (L.y + ${H.toFixed(2)}) / ${(2 * H).toFixed(2)});
      uv.x = (uv.x - ${(D / (2 * H)).toFixed(3)} * 0.5) + 0.5;
      if (uv.x > 0.0 && uv.x < 1.0) c = texture2D(uCover, uv).rgb * 0.42;
    }
    // the mirror room's floor: translucent over its reflection
    float alpha = 1.0;
    if (abs(vStyle - 4.0) < 0.5 && face < 0.5 && uReflect < 0.5) alpha = 0.55;

    vec3 lines = vec3(0.0);
    float he = 1.6;                                    // the box's edges: 3.2 px
    // the box edges on each face (the opening's frame glows when crossed)
    if (face < 1.5) {
      float y0 = 0.0;
      lines = max(lines, neon(px(a.x, -${W.toFixed(2)}, fwA), he, col, edgeGain));
      lines = max(lines, neon(px(a.x, ${W.toFixed(2)}, fwA), he, col, edgeGain));
      lines = max(lines, neon(px(a.y, 0.0, fwB), he, col, edgeGain * (1.0 + 2.0 * gFront)));
      lines = max(lines, neon(px(a.y, ${(-D).toFixed(2)}, fwB), he, col, edgeGain));
      // guide lines: two along, three across
      for (int i = 1; i < 4; i++) lines = max(lines, neon(px(a.y, ${(-D).toFixed(2)} * float(i) / 4.0, fwB), 0.7, col, 0.55 * fineFade(${(D / 4).toFixed(2)}, fwB)));
      lines = max(lines, neon(px(a.x, ${(-W / 3).toFixed(3)}, fwA), 0.7, col, 0.55 * fineFade(${(W / 3).toFixed(2)}, fwA)));
      lines = max(lines, neon(px(a.x, ${(W / 3).toFixed(3)}, fwA), 0.7, col, 0.55 * fineFade(${(W / 3).toFixed(2)}, fwA)));
    } else if (face < 3.5) {
      lines = max(lines, neon(px(a.y, -${H.toFixed(2)}, fwB), he, col, edgeGain));
      lines = max(lines, neon(px(a.y, ${H.toFixed(2)}, fwB), he, col, edgeGain));
      lines = max(lines, neon(px(a.x, 0.0, fwA), he, col, edgeGain * (1.0 + 2.0 * gFront)));
      lines = max(lines, neon(px(a.x, ${(-D).toFixed(2)}, fwA), he, col, edgeGain));
    } else if (face < 4.5) {
      lines = max(lines, neon(px(a.x, -${W.toFixed(2)}, fwA), he, col, edgeGain));
      lines = max(lines, neon(px(a.x, ${W.toFixed(2)}, fwA), he, col, edgeGain));
      lines = max(lines, neon(px(a.y, -${H.toFixed(2)}, fwB), he, col, edgeGain));
      lines = max(lines, neon(px(a.y, ${H.toFixed(2)}, fwB), he, col, edgeGain));
      // the door's frame
      float fw = max(fwA, fwB);
      lines = max(lines, neon(abs(dd) / fw, 1.9, col, edgeGain * (1.0 + 2.5 * gDoor)));
    }
    // the styles
    if (vStyle < 0.5) {
      // GRID: a fine grid on every face
      float g = 0.25;
      vec2 f = abs(fract(a / g + 0.5) - 0.5) * g;
      float fade = min(fineFade(g, fwA), fineFade(g, fwB));
      lines = max(lines, neon(f.x / max(fwA, 1e-7), 0.55, col, 0.45 * fade));
      lines = max(lines, neon(f.y / max(fwB, 1e-7), 0.55, col, 0.45 * fade));
    } else if (vStyle < 1.5) {
      // STRIPES: horizontal bands on the side walls, travelling toward the door
      if (face > 1.5 && face < 3.5) {
        float bandP = 0.4;
        float y = fract((a.y + 5.0) / bandP);
        float fade = fineFade(bandP, fwB);
        float run = fract((a.x * 0.6 - uTime * 0.5));
        c += col * 0.10 * step(0.5, y) * fade * (0.5 + 0.5 * step(0.5, run));
        lines = max(lines, neon(abs(fract((a.y + 5.0) / bandP + 0.5) - 0.5) * bandP / max(fwB, 1e-7), 0.6, col, 0.6 * fade));
      }
    } else if (vStyle < 2.5) {
      // ARCH: columns either side of the (arched) door
      if (face > 3.5 && face < 4.5) {
        for (int i = 0; i < 2; i++) {
          float xc = ${(K * W).toFixed(3)} + 0.16 + 0.16 * float(i);
          float m = step(a.y, ${(K * H + 0.35).toFixed(3)});
          lines = max(lines, neon(px(abs(a.x), xc, fwA), 1.2, col, 0.9 * m * edgeGain));
        }
        float topY = ${(K * H + 0.35).toFixed(3)};
        float m2 = step(abs(a.x), ${(K * W + 0.32).toFixed(3)});
        lines = max(lines, neon(px(a.y, topY, fwB), 1.2, col, 0.9 * m2 * edgeGain));
      }
    } else if (vStyle < 3.5) {
      // STAIRS: the ramp's steps (lines across), its sides
      if (face > 4.5) {
        float sN = 10.0, t = (a.y - ${(-D * 0.55).toFixed(3)}) / ${(-D * 0.45).toFixed(3)};
        float stepP = 1.0 / sN;
        float fwT = fwB / ${(D * 0.45).toFixed(3)};
        float fade = fineFade(stepP, fwT);
        lines = max(lines, neon(abs(fract(t * sN + 0.5) - 0.5) * stepP / max(fwT, 1e-7), 0.8, col, 0.8 * fade));
        lines = max(lines, neon(px(abs(a.x), ${(K * W).toFixed(3)}, fwA), 1.2, col, edgeGain));
        c = col * 0.06;
      }
    }
    vec3 outc = c + lines;
    if (uReflect > 0.5) outc *= 0.35;
    gl_FragColor = vec4(outc, max(alpha, min(1.0, max(lines.r, max(lines.g, lines.b)))));
  }
`.replace('D_PLUS_Z(L.z)', `(${D.toFixed(2)} + L.z)`);
const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const displayFragmentShader = `
  precision highp float;
  uniform sampler2D uTex;
  uniform float uFlash;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float att = mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, d));
    vec3 c = texture2D(uTex, vUv).rgb * att;
    c = mix(c, vec3(${VDJ_DROP_FLASH.toFixed(2)}), uFlash);
    gl_FragColor = vec4(c, 1.0);
  }
`;

export class DoorwaysVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 1;
    this._L = Object.assign({}, TIER[this._tier]);
    const fs = urlParam('doorstyle');
    this._forceStyle = fs && STYLES.includes(fs) ? STYLES.indexOf(fs) : null;
    const dp = parseInt(urlParam('doordepth'), 10);
    if (Number.isFinite(dp)) this._L.ahead = Math.max(1, Math.min(6, dp));
    this._forceDrop = urlParam('doordrop') === '1';
    this._forceBuild = urlParam('doorbuild') === '1';
    this._post = new VdjPost({ bloomStrength: 0.28, bloomThreshold: 0.80, grain: 0, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
    if (urlParam('doorprobe') === '1' && typeof window !== 'undefined') window.__doorways = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    const L = this._L;
    this._cam = new THREE.PerspectiveCamera(FOV_DEG, 1.78, 0.01, 800);
    this._private = new THREE.Scene();
    const rooms = []; for (let n = -N_BACK; n <= 6; n++) rooms.push(n);
    this._black = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat); this._black.needsUpdate = true;
    const U = this._U = {
      uStyle: { value: new Array(N_BACK + 7).fill(0) }, uColor: { value: Array.from({ length: N_BACK + 7 }, () => new THREE.Color()) },
      uAhead: { value: L.ahead }, uReflect: { value: 0 }, uShape: { value: 0 }, uT: { value: 0 }, uPulse: { value: 0 }, uLit: { value: 0 },
      uTime: { value: 0 }, uCover: { value: this._black }, uHasCover: { value: 0 }
    };
    this._geom = roomGeometry(rooms);
    const mk = (reflect) => {
      const u = Object.assign({}, U, { uReflect: { value: reflect } });
      return new THREE.ShaderMaterial({ uniforms: u, vertexShader: roomVertexShader, fragmentShader: roomFragmentShader, side: THREE.DoubleSide, transparent: true });
    };
    this._mirrorMesh = new THREE.Mesh(this._geom, mk(1));
    this._mirrorMesh.frustumCulled = false; this._mirrorMesh.renderOrder = 0; this._mirrorMesh.visible = L.mirror;
    this._rooms = new THREE.Mesh(this._geom, mk(0));
    this._rooms.frustumCulled = false; this._rooms.renderOrder = 1;
    this._private.add(this._mirrorMesh, this._rooms);

    this._msaaTarget = null;
    this._dispU = {
      uTex: { value: null }, uFlash: { value: 0 },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._dispU, vertexShader: quadVertexShader, fragmentShader: displayFragmentShader, depthTest: false, depthWrite: false
    }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    // state
    this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM; this._time = 0;
    this._R = 0;                 // rooms travelled (integer part: the absolute depth of room 0)
    this._offset = 0;            // R = clock / 4 + offset at the normal pace (integer: crossings on the downbeats)
    this._align = null;
    this._shapeIdx = 0; this._palShift = 0; this._lastPhraseAt = 0;
    this._drop = null; this._flashFrames = 0; this._yaw = 0;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._coverUrl = undefined; this._paletteKey = null;
    this._log = [];
  }

  _note(e) { this._log.push(e); if (this._log.length > 40) this._log.shift(); }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  _palette(profile) {
    const pal = profile.artworkPalette;
    const src = (pal && (pal.vividColors || pal.dominantColors)) || [];
    const cols = [];
    for (const h of src.slice(0, 4)) {
      const c = vdjColor(h), hsl = { h: 0, s: 0, l: 0 }; c.getHSL(hsl);
      if (cols.every((x) => { const g = { h: 0, s: 0, l: 0 }; x.getHSL(g); const d = Math.abs(g.h - hsl.h); return Math.min(d, 1 - d) >= 0.18; })) cols.push(c);
    }
    const base = cols.length ? (() => { const g = { h: 0, s: 0, l: 0 }; cols[0].getHSL(g); return g.h; })() : 0.83;
    for (let k = 1; cols.length < 3; k++) cols.push(new THREE.Color().setHSL((base + k * 0.33) % 1, 0.9, 0.55));
    this._cols = cols;
  }

  _requestCover(profile) {
    const url = profile.artworkUrl || null;
    if (url === this._coverUrl) return;
    this._coverUrl = url;
    if (!url) { this._U.uHasCover.value = 0; return; }
    const self = this;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (tex) => {
      if (!self._scene || url !== self._coverUrl) { tex.dispose(); return; }
      if (self._coverTex) self._coverTex.dispose();
      self._coverTex = tex; self._U.uCover.value = tex; self._U.uHasCover.value = 1;
    }, () => { self._U.uHasCover.value = 0; });
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const U = this._U, L = this._L;
    this._requestCover(profile);
    const pk = (profile.artworkPalette && String((profile.artworkPalette.vividColors || []).slice(0, 4))) || '';
    if (pk !== this._paletteKey) { this._paletteKey = pk; this._palette(profile); }
    if (profile.playbackState !== 'PLAYING') { this._dispU.uFlash.value = 0; this._applyRooms(); return; }   // frozen, never black
    this._time += dt;

    // the clock, in beats, on the music's bars
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase, ph = profile.phrasePhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? bpm : NO_BPM;
    const beatS = 60 / this._bpm;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > SNAP_BEATS) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else this._clock += Math.max(dt / beatS * 0.5, dt / beatS + err * Math.min(1, dt / 0.5));
    } else { this._clock += dt / beatS; this._targetOffset = null; }
    const c = this._clock, cp = this._clockPrev;
    const edge = (k) => Math.floor(cp / k) !== Math.floor(c / k) && c > cp;

    // the rise: a room per beat, then per half beat
    const nx = profile.director && profile.director.nextEvent;
    let build = 0;
    if (nx && nx.type === 'drop' && nx.score >= DROP_SCORE_MIN && nx.msUntil > 0) build = Math.max(0, 1 - nx.msUntil / 1000 / (BUILD_BEATS * beatS));
    if (this._forceBuild) build = 0.5 + 0.5 * Math.sin(this._time * 0.3);

    // the phrase: the next palette, the doors change shape
    let phraseEdge = false;
    if (hasGrid && ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phraseEdge = true; this._phasePhrase = ph; }
    else if (edge(16)) phraseEdge = true;
    if (!phraseEdge && edge(4) && c - this._lastPhraseAt >= 32) phraseEdge = true;
    if (phraseEdge) {
      this._lastPhraseAt = c; this._palShift++; this._shapeIdx = (this._shapeIdx + 1) % SHAPES.length;
      this._note({ what: 'phrase', beat: +c.toFixed(2), shape: SHAPES[this._shapeIdx] });
    }

    // the drop: every room lit, flash, the camera turns round a moment, then on
    let drop = hasGrid && this._tickDrops(profile);
    if (this._forceDrop && edge(4) && Math.floor(c / 4) % 8 === 4) drop = true;
    if (drop && !this._drop) {
      this._drop = { start: c };
      if (this._comfort.requestFlash(this._time)) this._flashFrames = 1;
      this._note({ what: 'drop', beat: +c.toFixed(2) });
    }
    let lit = 0, yaw = 0, paused = false;
    if (this._drop) {
      const t = c - this._drop.start;
      lit = t < DROP_LIT_BEATS ? Math.max(0, 1 - t / DROP_LIT_BEATS) : 0;
      const t1 = TURN_BEATS, t2 = t1 + TURN_HOLD, t3 = t2 + TURN_BEATS;
      if (t < t1) yaw = Math.PI * smooth(t / t1);
      else if (t < t2) yaw = Math.PI;
      else if (t < t3) yaw = Math.PI * (1 - smooth((t - t2) / TURN_BEATS));
      paused = t < t3;
      if (t >= DROP_LIT_BEATS && t >= t3) { this._drop = null; this._align = { start: c, from: this._R - c / 4 }; }
    }

    // the motion: R rooms travelled. Normal pace: R = clock / 4 + integer offset (a door on each downbeat);
    // the rise integrates faster; after a pause or a rise, the offset eases back to an integer in a bar
    const normal = !paused && build === 0;
    if (!normal) {
      const rate = paused ? 0 : (build < 0.5 ? 1 : 2);        // rooms per beat
      this._R += (c - cp) * (paused ? 0 : rate);
      this._align = { start: c, from: this._R - c / 4 };
    } else if (this._align) {
      const u = Math.min(1, (c - this._align.start) / ALIGN_BEATS);
      const target = Math.ceil(this._align.from - 1e-6);
      this._R = c / 4 + this._align.from + (target - this._align.from) * smooth(u);
      if (u >= 1) { this._offset = target; this._align = null; }
    } else this._R = c / 4 + this._offset;

    U.uLit.value = lit;
    this._yaw = yaw;
    U.uPulse.value = Math.exp(-(c - Math.floor(c)) * beatS / 0.12);
    U.uTime.value = c;
    this._post.update(vdjBeatChroma(c - Math.floor(c), beatS));
    this._dispU.uFlash.value = this._flashFrames > 0 ? 1 : 0;
    if (this._flashFrames > 0) this._flashFrames--;
    this._applyRooms();
  }

  _applyRooms() {
    const U = this._U, a0 = Math.floor(this._R + 1e-9);
    U.uT.value = this._R - a0;
    U.uShape.value = this._shapeIdx;
    const cols = this._cols || [new THREE.Color(1, 0, 0.6), new THREE.Color(0, 0.7, 1), new THREE.Color(1, 0.8, 0)];
    for (let n = -N_BACK; n <= 6; n++) {
      const abs = a0 + n, slot = n + N_BACK;
      U.uStyle.value[slot] = this._forceStyle != null ? this._forceStyle : styleAt(abs, this._L.mirror);
      // alternate colours by depth, the palette shifted on the phrases
      U.uColor.value[slot].copy(cols[(((abs + this._palShift) % cols.length) + cols.length) % cols.length]);
    }
  }

  _render(renderer) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_size); w = b.x; h = b.y; }
    const cam = this._cam;
    cam.aspect = w / h; cam.updateProjectionMatrix();
    // the camera: on the axis (the door's centre at the stage centre, R19), the exponential law
    const t = this._U.uT.value;
    const z = cameraZ(t);
    const sway = TAU * this._clock / 16;
    const st = safeZoneUniforms.uStageCenter.value;
    // a gentle sway: +-1 degree of yaw / pitch, roll under 3 degrees
    const yaw = 0.017 * Math.sin(sway) + this._yaw, pitch = 0.012 * Math.sin(sway * 0.5 + 1), roll = 0.05 * Math.sin(sway * 0.25);
    cam.position.set(0, 0, z);
    cam.rotation.set(pitch, yaw, roll, 'YXZ');
    cam.updateMatrixWorld();
    // the stage centre (R19): shift the image so the axis lands there
    cam.setViewOffset(w, h, -st.x * h / 2, st.y * h / 2, w, h);
    if (!this._msaaTarget || this._msaaTarget.width !== w || this._msaaTarget.height !== h) {
      if (this._msaaTarget) this._msaaTarget.dispose();
      this._msaaTarget = new THREE.WebGLRenderTarget(w, h, { samples: this._L.mirror ? 4 : 0, depthBuffer: true, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    }
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    renderer.setRenderTarget(this._msaaTarget);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    renderer.render(this._private, cam);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
    this._dispU.uTex.value = this._msaaTarget.texture;
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
    for (const m of [this._display]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    if (this._rooms) this._rooms.material.dispose();
    if (this._mirrorMesh) this._mirrorMesh.material.dispose();
    if (this._geom) this._geom.dispose();
    if (this._coverTex) this._coverTex.dispose();
    if (this._black) this._black.dispose();
    if (this._msaaTarget) this._msaaTarget.dispose();
    if (typeof window !== 'undefined' && window.__doorways === this) window.__doorways = null;
    this._scene = null;
  }
}

function smooth(x) { const t = Math.max(0, Math.min(1, x)); return t * t * (3 - 2 * t); }
const _size = new THREE.Vector2();
const _cc = new THREE.Color();
